import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Form, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import apiCall from "../../API/API";
import ExportButtons, { collectPages } from "../../Component/ExportButtons/ExportButtons";
import "../PlanSettings/PlanSettings.css";
import "../ActivationRequests/ActivationRequests.css";

/** Income sources shown in the admin, with their names and what the table explains. */
export const INCOMES = {
  roi_income: { name: "Growth On Income", from: "Package", note: "Monthly growth on members' own packages." },
  direct_income: { name: "Direct Income (One Time)", from: "From member", note: "One time income on team packages, paid by rank difference." },
  career_recurring_bonus: { name: "Career Recurring Bonus", from: "From member", note: "Recurring income on the team's growth, paid by rank difference." },
  over_ride_bonus: { name: "Over Ride Bonus", from: "From member", note: "Sponsor's share of income earned by a same-or-higher rank direct." },
  reward_income: { name: "Reward Income", from: "Reward", note: "Rewards paid as cash from Reward Achievers." },
  royalty_income: { name: "Royalty Income", from: "From member", note: "Monthly royalty for reward levels." },
};

/** "all" lists every income above together. */
const ALL = { name: "Total Income", from: "From", note: "Every income credited to members." };

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const EMPTY = { username: "", startDate: "", endDate: "" };
const LIMIT = 20;

/** Credits of one income for every member, with filters, totals and an Excel (CSV) export. */
const IncomeReport = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isAll = slug === "all";
  const meta = isAll ? ALL : INCOMES[slug] || { name: String(slug || "").replace(/_/g, " "), from: "From", note: "" };
  const [filters, setFilters] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [sum, setSum] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const query = useCallback(
    (p, limit) => {
      const params = new URLSearchParams({ debit_credit: "credit", page: p, limit });
      (isAll ? Object.keys(INCOMES) : [slug]).forEach((s) => params.append("source", s));
      Object.entries(applied).forEach(([k, v]) => v && params.set(k, v));
      return `/get-payment-transaction?${params.toString()}`;
    },
    [slug, isAll, applied]
  );

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await apiCall("GET", query(page, LIMIT));
    setLoading(false);
    if (error) return toast.error(error.message || "Could not load the report.");
    setRows(data?.data || []);
    setTotal(data?.totalRecords || 0);
    setPages(Math.max(1, data?.totalPages || 1));
    setSum(data?.creditSum ?? data?.totalSum ?? 0);
  }, [query, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [slug]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setApplied(filters);
    }, 350);
    return () => clearTimeout(t);
  }, [filters]);

  const exportColumns = [
    { header: "Date", value: (r) => moment(r.time || r.createdAt).format("DD MMM YYYY, hh:mm A") },
    ...(isAll ? [{ header: "Income", value: (r) => INCOMES[r.source]?.name || r.source }] : []),
    { header: "Member", value: (r) => r.name },
    { header: "Username", value: (r) => r.username },
    { header: meta.from, value: (r) => (r.source === "roi_income" ? (r.order_Id ? `Order #${r.order_Id}` : "") : r.from && r.from !== r.username ? `${r.from_name || ""} (${r.from})` : "") },
    { header: "Base", value: (r) => (r.order_amount ? inr(r.order_amount) : "") },
    { header: "%", value: (r) => (r.income_percent ? `${r.income_percent}%` : "") },
    { header: "Level", value: (r) => r.level || "" },
    { header: "Amount", value: (r) => inr(r.amount) },
  ];
  const exportRows = () =>
    collectPages(async (p, l) => {
      const { data } = await apiCall("GET", query(p, l));
      return { rows: data?.data || [], pages: data?.totalPages || 1 };
    }, { limit: 500 });

  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));
  const active = Object.values(applied).filter(Boolean).length;

  return (
    <div className="plan-settings activation-requests-page">
      <div className="ps-header">
        <div>
          <h2>{meta.name}</h2>
          <p>{meta.note}</p>
        </div>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <Button variant="outline-secondary" onClick={() => navigate("/dashboard/payout_controls")}>All incomes</Button>
          <ExportButtons title={meta.name} columns={exportColumns} getRows={exportRows} filters={Object.entries(applied).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(", ")} disabled={!total} />
        </div>
      </div>

      <div className="ps-stats ar-stats">
        <div className="ps-stat ar-stat ar-stat--approved">
          <span>Total credited{active ? " (filtered)" : ""}</span>
          <strong>{inr(sum)}</strong>
          <small>{meta.name}</small>
        </div>
        <div className="ps-stat ar-stat">
          <span>Entries</span>
          <strong>{total}</strong>
          <small>credits</small>
        </div>
        <div className="ps-stat ar-stat">
          <span>Average</span>
          <strong>{inr(total ? sum / total : 0)}</strong>
          <small>per credit</small>
        </div>
      </div>

      <div className="ps-panel" style={{ borderTop: "1px solid var(--ps-border)", borderRadius: "var(--ps-radius)" }}>
        <div className="ar-filters">
          <div className="ar-f-search">
            <Form.Label>Member</Form.Label>
            <Form.Control size="sm" placeholder="Username" value={filters.username} onChange={set("username")} />
          </div>
          <div>
            <Form.Label>From</Form.Label>
            <Form.Control size="sm" type="date" value={filters.startDate} onChange={set("startDate")} />
          </div>
          <div>
            <Form.Label>To</Form.Label>
            <Form.Control size="sm" type="date" value={filters.endDate} onChange={set("endDate")} />
          </div>
          <div className="ar-f-reset">
            <Button size="sm" variant="outline-secondary" disabled={!active} onClick={() => setFilters(EMPTY)}>Reset</Button>
          </div>
        </div>

        <div className="ps-table-wrap">
          <Table className="ps-table">
            <thead>
              <tr>
                <th>Date</th>
                {isAll && <th>Income</th>}
                <th>Member</th>
                <th>{meta.from}</th>
                <th>Base</th>
                <th>%</th>
                <th>Level</th>
                <th className="text-end">Amount</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={isAll ? 8 : 7} className="ps-empty"><Spinner size="sm" /> Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={isAll ? 8 : 7} className="ps-empty">{active ? "Nothing matches these filters." : `No ${meta.name} credited yet.`}</td></tr>
              )}
              {!loading &&
                rows.map((r) => (
                  <tr key={r._id || r.tx_Id}>
                    <td className="ps-small">
                      <div className="ps-strong">{moment(r.time || r.createdAt).format("DD MMM YYYY")}</div>
                      <div className="ps-muted">{moment(r.time || r.createdAt).format("hh:mm A")}</div>
                    </td>
                    {isAll && <td className="ps-small ps-strong">{INCOMES[r.source]?.name || r.source}</td>}
                    <td>
                      <div className="ps-strong">{r.name || "—"}</div>
                      <div className="ps-muted ps-small">{r.username}</div>
                    </td>
                    <td className="ps-small">
                      {r.source === "roi_income" ? (r.order_Id ? `Order #${r.order_Id}` : "—") : r.from && r.from !== r.username ? (
                        <>
                          <div className="ps-strong">{r.from_name || r.from}</div>
                          <div className="ps-muted">{r.from}</div>
                        </>
                      ) : "—"}
                    </td>
                    <td>{r.order_amount ? inr(r.order_amount) : "—"}</td>
                    <td>{r.income_percent ? `${r.income_percent}%` : "—"}</td>
                    <td>{r.level ? r.level : "—"}</td>
                    <td className="text-end ps-strong">{inr(r.amount)}</td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>

        <div className="ar-pagination">
          <div className="ar-pagination-info">
            Showing <strong>{total ? (page - 1) * LIMIT + 1 : 0}–{Math.min(page * LIMIT, total)}</strong> of <strong>{total}</strong>
          </div>
          <div className="ar-pages">
            <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <span className="ps-muted ps-small">Page {page} of {pages}</span>
            <Button size="sm" variant="outline-secondary" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncomeReport;
